/**
 * useDealTags - Hook para gerenciar tags de negócios do CRM
 * 
 * Versão: 1.0.0
 * - CRUD de tags do tenant
 * - Atribuição/remoção de tags em deals
 * - Cache via React Query
 * 
 * RELEASE: 2026-01-07
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { createLogger } from '@/lib/logger';

// Logger condicional
const logger = createLogger('useDealTags');

// ============================================
// TIPOS
// ============================================

/** Tag de negócio */
export interface DealTag {
  id: string;
  tenant_id: string;
  name: string;
  color: string;
  created_at: string;
  updated_at: string;
}

/** Atribuição de tag a deal */
export interface DealTagAssignment {
  deal_id: string;
  tag_id: string;
  assigned_by: string | null;
  assigned_at: string;
  tag?: DealTag;
}

/** Dados para criar tag */
export interface CreateDealTagData {
  tenant_id: string;
  name: string;
  color: string;
}

/** Dados para atualizar tag */
export interface UpdateDealTagData {
  id: string;
  name?: string;
  color?: string;
}

// ============================================
// HOOK: Lista tags do tenant
// ============================================

export function useDealTags(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['deal-tags', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      logger.debug('Buscando tags do tenant via RPC', { tenantId });

      // RPC server-side (substitui SELECT direto a crm_deal_tags)
      const { data, error } = await supabase.rpc('get_tenant_deal_tags', {
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao buscar tags', error);
        throw error;
      }

      return (data || []) as DealTag[];
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
}

// ============================================
// HOOK: Lista tags atribuídas a um deal
// ============================================

export function useDealTagAssignments(dealId: string | undefined) {
  const { currentTenant } = useAuth();

  return useQuery({
    queryKey: ['deal-tag-assignments', dealId, currentTenant?.id],
    queryFn: async () => {
      if (!dealId || !currentTenant?.id) return [];

      logger.debug('Buscando tags do deal via RPC batch', { dealId });

      // Reaproveita RPC batch existente (passa array de 1 elemento)
      const { data, error } = await supabase.rpc('get_deals_tag_assignments', {
        p_deal_ids: [dealId],
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('Erro ao buscar atribuições', error);
        throw error;
      }

      // Mapeia retorno (tag_name/tag_color inline) para o shape DealTagAssignment
      return (data || []).map((item: any) => ({
        deal_id: item.deal_id,
        tag_id: item.tag_id,
        assigned_by: item.assigned_by,
        assigned_at: item.assigned_at,
        tag: {
          id: item.tag_id,
          tenant_id: currentTenant.id,
          name: item.tag_name,
          color: item.tag_color,
          created_at: item.assigned_at,
          updated_at: item.assigned_at,
        } as DealTag,
      })) as DealTagAssignment[];
    },
    enabled: !!dealId && !!currentTenant?.id,
    staleTime: 30 * 1000, // 30 segundos
  });
}

// ============================================
// MUTATION: Criar tag
// ============================================

export function useCreateDealTag() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (data: CreateDealTagData) => {
      logger.info('Criando tag', { name: data.name });
      
      const { data: result, error } = await supabase
        .from('crm_deal_tags')
        .insert(data)
        .select()
        .single();
      
      if (error) {
        logger.error('Erro ao criar tag', error);
        throw error;
      }
      
      return result as DealTag;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['deal-tags', data.tenant_id] });
      toast({
        title: 'Tag criada',
        description: `Tag "${data.name}" criada com sucesso.`,
      });
    },
    onError: (error: Error) => {
      // Verificar duplicidade
      if (error.message?.includes('unique') || error.message?.includes('duplicate')) {
        toast({
          variant: 'destructive',
          title: 'Tag duplicada',
          description: 'Já existe uma tag com esse nome.',
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Erro ao criar tag',
          description: error.message,
        });
      }
    },
  });
}

// ============================================
// MUTATION: Atualizar tag
// ============================================

export function useUpdateDealTag() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (data: UpdateDealTagData) => {
      logger.info('Atualizando tag', { id: data.id });
      
      const { id, ...updateData } = data;
      
      const { data: result, error } = await supabase
        .from('crm_deal_tags')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        logger.error('Erro ao atualizar tag', error);
        throw error;
      }
      
      return result as DealTag;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['deal-tags', data.tenant_id] });
      queryClient.invalidateQueries({ queryKey: ['deal-tag-assignments'] });
      toast({
        title: 'Tag atualizada',
        description: `Tag "${data.name}" atualizada com sucesso.`,
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Erro ao atualizar tag',
        description: error.message,
      });
    },
  });
}

// ============================================
// MUTATION: Deletar tag
// ============================================

export function useDeleteDealTag() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async ({ id, tenantId }: { id: string; tenantId: string }) => {
      logger.info('Deletando tag', { id });
      
      const { error } = await supabase
        .from('crm_deal_tags')
        .delete()
        .eq('id', id);
      
      if (error) {
        logger.error('Erro ao deletar tag', error);
        throw error;
      }
      
      return { id, tenantId };
    },
    onSuccess: ({ tenantId }) => {
      queryClient.invalidateQueries({ queryKey: ['deal-tags', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['deal-tag-assignments'] });
      toast({
        title: 'Tag excluída',
        description: 'Tag removida com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Erro ao excluir tag',
        description: error.message,
      });
    },
  });
}

// ============================================
// MUTATION: Atribuir tags a um deal
// ============================================

interface AssignTagsData {
  dealId: string;
  tagIds: string[];
  tenantId: string;
}

export function useAssignDealTags() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async ({ dealId, tagIds, tenantId }: AssignTagsData) => {
      logger.info('Sincronizando tags do deal via RPC', { dealId, tagIds });

      // RPC atômica server-side (substitui SELECT + DELETE + INSERT diretos)
      const { data, error } = await supabase.rpc('sync_deal_tag_assignments', {
        p_deal_id: dealId,
        p_tag_ids: tagIds,
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao sincronizar tags', error);
        throw error;
      }

      // RPC retorna TABLE(added, removed) -> array com 1 linha
      const row = Array.isArray(data) && data.length > 0 ? data[0] : { added: 0, removed: 0 };
      return { dealId, added: Number(row.added) || 0, removed: Number(row.removed) || 0 };
    },
    onSuccess: ({ dealId, added, removed }) => {
      queryClient.invalidateQueries({ queryKey: ['deal-tag-assignments', dealId] });

      if (added > 0 || removed > 0) {
        toast({
          title: 'Tags atualizadas',
          description: `${added} adicionada(s), ${removed} removida(s).`,
        });
      }
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Erro ao atualizar tags',
        description: error.message,
      });
    },
  });
}
